import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Tooltip,
  TextField,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import {
  Add,
  Cancel,
  Delete,
  Edit,
  Save,
} from "@mui/icons-material";
import toast from "react-hot-toast";

/* ------------------------- Utility Functions ------------------------- */
const validateRow = (row) => {
  const errors = {};
  if (!row.name) errors.name = "Name is required";
  if (!row.description) errors.description = "Description is required";
  if (!row.iconCode) errors.iconCode = "Icon is required";
  if (!row.passRate) errors.passRate = "Pass Rate is required";
  return errors;
};

/* ------------------------- Toolbar Component ------------------------- */
const EditToolbar = ({ onAddNewRecord, setRowModesModel, total, active, onAll, onValid, onInvalid, validCount, invalidCount }) => { 
  const handleClick = () => { 
    onAddNewRecord(); 
  }; 
  return (
    <Stack direction={'row'} justifyContent={'space-between'} sx={{ p: 1 }}> 
      <Button onClick={handleClick} startIcon={<Add />}> 
      Add Record 
      </Button> 
      <Stack direction={'row'} alignItems={'center'}> 
        <Typography variant="body" pr={4}> {total} records </Typography> 
      </Stack> 
    </Stack>
  ); 
}

/* ------------------------- Main Component ------------------------- */
const UploadGridValueTags = ({ onDataChange }) => {
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  /* ------------------------- Handlers ------------------------- */
  const updateRows = (newRows) => {
    console.log('newRows', newRows);
    
    setRows(newRows);
    onDataChange?.(newRows);
  };

  const handleRowEditStop = (params, event) => {
    console.log('params', params);
    console.log('event', event);
    
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () =>
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });

  const handleSaveClick = (id) => () => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    const errors = validateRow(row);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix errors before saving.");
      updateRows(rows.map((r) => (r.id === id ? { ...r, errors } : r)));
      return;
    }

    updateRows(rows.map((r) => (r.id === id ? { ...r, errors: {} } : r)));
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () =>
    updateRows(rows.filter((r) => r.id !== id));

  const handleCancelClick = (id) => () => {
    const row = rows.find((r) => r.id === id);
    if (row?.isNew) updateRows(rows.filter((r) => r.id !== id));
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleAddNewRecord = () => {
    if (rows.length >= 5) {
      toast.error("You can only add up to 5 records.");
      return;
    }
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    const newRow = {
      id: newId,
      name: "",
      description: "",
      iconCode: "",
      passRate: "",
      isNew: true,
    };

    updateRows([...rows, newRow]);
    setRowModesModel({
      ...rowModesModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: "name" },
    });
  };

  const processRowUpdate = (newRow) => {
    const errors = validateRow(newRow);
    if (Object.keys(errors).length > 0) toast.error("Fix validation errors before saving.");
    const updatedRow = { ...newRow, errors };
    updateRows(rows.map((r) => (r.id === newRow.id ? updatedRow : r)));
    return updatedRow;
  };

  const makeHandleChange = (params, field) => (e) => {
    const value = e.target.value;
    const errorMessage = validateField(field, value);

    params.api.setEditCellValue({
      id: params.id,
      field,
      value,
    });

    // Update the specific row in allRows
    const updatedAllRows = rows.map((row) =>
      row.id === params.id
        ? { ...row, [field]: value, errors: { ...(row.errors || {}), [field]: errorMessage } }
        : row
    );

    handleDataChange(updatedAllRows);
  };


  const validateField = (field, value) => {
    switch (field) {
      case "name":
        if (!value?.trim()) return "Name is required";
        return "";
      case "code":
        if (!value?.trim()) return "Code is required";
        return "";
      case "iconCode":
        if (!value?.trim()) return "Icon is required";
        return "";
      case "passRate":
        if (!value) return "Pass rate is required";
        return "";
      default:
        return "";
    }
  };

  const handleDataChange = (rows) => {
    setRows(rows);
    onDataChange?.(rows);
  };

  /* ------------------------- Columns ------------------------- */
  const columns = [
    { field: "name", 
      headerName: "Name", 
      flex: 1, 
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.name || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "name")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    { field: "description", 
      headerName: "Description", 
      flex: 1.2, 
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.description || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "description")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "iconCode",
      headerName: "Icon",
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const iconClass = params.value?.startsWith("lni")
          ? params.value
          : `lni lni-${params.value}`;
        return <i className={iconClass} style={{ fontSize: 20 }} />;
      },
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.iconCode || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "iconCode")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    { field: "passRate", 
      headerName: "Pass Rate", 
      flex: 0.8, 
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.passRate || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "passRate")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: ({ id }) => {
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        return isEditing
          ? [
            <GridActionsCellItem icon={<Save />} label="Save" onClick={handleSaveClick(id)} />,
            <GridActionsCellItem icon={<Cancel />} label="Cancel" onClick={handleCancelClick(id)} />,
          ]
          : [
            <GridActionsCellItem icon={<Edit />} label="Edit" onClick={handleEditClick(id)} />,
            <GridActionsCellItem icon={<Delete />} label="Delete" onClick={handleDeleteClick(id)} />,
          ];
      },
    },
  ];

  /* ------------------------- Render ------------------------- */
  return (
    <Box sx={{ alignItems: "center" }}>
      <Typography sx={{ fontWeight: 500, mb: 1 }}>Value Tags</Typography>

      <DataGrid
              columns={columns}
              rows={rows}
              getCellClassName={(params) => {
                const rowErrors = params.row.errors || {};
                return rowErrors[params.field] ? "cell-error" : "";
              }}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              showToolbar
              pageSizeOptions={[5, 10, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              slots={{
                toolbar: EditToolbar
              }}
              slotProps={{
                toolbar: {
                  total: rows.length,
                  onAddNewRecord: handleAddNewRecord, // Add this function
                  setRowModesModel,
                },
              }}
              disableRowSelectionOnClick
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 0,
                "& .MuiDataGrid-row": {
                  backgroundColor: "transparent",
                  borderBottom: "1px solid #ccc !important",
                },
                "& .MuiDataGrid-cell": {
                  borderRight: "1px solid #ccc !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "1px solid #ccc !important",
                },
              }}
            />
    </Box>
  );
};

export default UploadGridValueTags;
